import { useEffect, useRef } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { HelpCircle } from 'lucide-react';

const TOUR_SEEN_KEY = 'la360_demo_tour_v1';

// Steps reference elements by their existing data-testid on the demo dashboard.
// Steps whose element is not on the page (e.g. sidebar hidden on mobile) are skipped.
const STEP_DEFS = [
  {
    popover: {
      title: 'Welcome to the live demo 👋',
      description: "This is a fully interactive sample of the Law Agency 360 dashboard. Here's a quick tour of what's inside - leave anytime with the ✕ or Esc.",
    },
  },
  {
    el: '[data-testid="demo-nav-overview"]',
    popover: { title: 'Overview', description: 'A high-level snapshot of the firm\'s performance - AI citations, rankings and organic growth at a glance.' },
  },
  {
    el: '[data-testid="demo-nav-keywords"]',
    popover: { title: 'Keywords & Queries', description: 'The keywords and AI prompts the firm is being found for across search and AI assistants.' },
  },
  {
    el: '[data-testid="demo-nav-position-tracking"]',
    popover: { title: 'Position Tracking', description: 'Track rankings across search engines and AI assistants over time.' },
  },
  {
    el: '[data-testid="demo-nav-traffic-analytics"]',
    popover: { title: 'Traffic Analytics', description: 'See how visitors find and engage with the website.' },
  },
  {
    el: '[data-testid="demo-addons-section"]',
    popover: { title: 'Add-ons', description: 'Optional services - Social Media, Paid Ads, AI Video and Website Creation - all managed in one place.' },
  },
  {
    el: '[data-testid="demo-chat-bubble"]',
    popover: { title: 'Chat with your team', description: 'Message the specialists working on the account directly - no scattered email threads.', side: 'left', align: 'end' },
  },
  {
    popover: {
      title: 'Like what you see? 🎉',
      description: 'This is just a demo. Sign up to get your own dashboard and a free Growth Snapshot. Replay this tour anytime with the "Take a tour" button.',
    },
  },
];

function buildSteps() {
  return STEP_DEFS
    .filter((s) => !s.el || document.querySelector(s.el))
    .map((s) => (s.el ? { element: s.el, popover: s.popover } : { popover: s.popover }));
}

function startTour(onDone) {
  const steps = buildSteps();
  if (!steps.length) return;
  const d = driver({
    showProgress: true,
    allowClose: true,
    overlayOpacity: 0, // no dim / no blur of the background, per requirement
    stagePadding: 6,
    showButtons: ['next', 'previous', 'close'],
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Done',
    progressText: '{{current}} of {{total}}',
    steps,
    onDestroyed: () => { if (onDone) onDone(); },
  });
  d.drive();
}

export default function DemoTour() {
  const startedRef = useRef(false);

  useEffect(() => {
    let seen = false;
    try { seen = localStorage.getItem(TOUR_SEEN_KEY) === '1'; } catch (_e) { seen = false; }
    if (seen || startedRef.current) return undefined;
    startedRef.current = true;
    const t = setTimeout(() => {
      startTour(() => { try { localStorage.setItem(TOUR_SEEN_KEY, '1'); } catch (_e) { /* ignore */ } });
    }, 900);
    return () => clearTimeout(t);
  }, []);

  return (
    <button
      type="button"
      onClick={() => startTour(() => { try { localStorage.setItem(TOUR_SEEN_KEY, '1'); } catch (_e) { /* ignore */ } })}
      className="fixed bottom-6 left-6 md:left-[300px] z-40 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/90 backdrop-blur-sm border border-slate-200 shadow-lg shadow-black/10 text-slate-700 text-sm font-semibold hover:bg-white hover:text-violet-700 transition-all"
      data-testid="demo-take-a-tour-btn"
    >
      <HelpCircle className="w-4 h-4" />
      Take a tour
    </button>
  );
}
